import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { storage, STORAGE_KEYS } from '../utils/storage';

interface Language {
    code: string;
    name: string;
    nativeName: string;
}

const languages: Language[] = [
    { code: 'en', name: 'English', nativeName: 'English' },
    { code: 'hi', name: 'Hindi', nativeName: 'हिंदी' },
    { code: 'gu', name: 'Gujarati', nativeName: 'ગુજરાતી' }
];

interface TranslationContextType {
    currentLanguage: string;
    setLanguage: (langCode: string) => void;
    isTranslating: boolean;
    languages: Language[];
    isGoogleTranslateReady: boolean;
}

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

// Google Translate declarations
declare global {
    interface Window {
        google?: {
            translate: {
                TranslateElement: any;
            };
        };
        googleTranslateElementInit?: () => void;
    }
}

interface TranslationProviderProps {
    children: ReactNode;
}

export const TranslationProvider: React.FC<TranslationProviderProps> = ({ children }) => {
    const [currentLanguage, setCurrentLanguage] = useState<string>(() => {
        // Get saved language from storage or default to 'en'
        return storage.getItemSync<string>(STORAGE_KEYS.SITE_LANGUAGE) || 'en';
    });
    const [isTranslating, setIsTranslating] = useState<boolean>(false);
    const [isGoogleTranslateReady, setIsGoogleTranslateReady] = useState<boolean>(false);

    // Guard React against DOM mutations introduced by Google Translate (prevents Node.removeChild errors)
    useEffect(() => {
        const origRemoveChild = Node.prototype.removeChild;
        const origInsertBefore = Node.prototype.insertBefore as unknown as (
            this: Node,
            newNode: Node,
            referenceNode: Node | null
        ) => Node;
        const origReplaceChild = Node.prototype.replaceChild as unknown as (
            this: Node,
            newChild: Node,
            oldChild: Node
        ) => Node;

        // Patch removeChild to fallback to the node's actual parent if Google Translate re-parented it
        // and avoid throwing which crashes React reconciliation.
        Node.prototype.removeChild = function patchedRemoveChild<T extends Node>(this: Node, child: T): T {
            try {
                if (child && child.parentNode !== this) {
                    // If the child has a different parent (e.g., moved by GT), attempt removal there.
                    if (child.parentNode) {
                        // @ts-ignore - call with actual parent context
                        return origRemoveChild.call(child.parentNode, child) as unknown as T;
                    }
                    // If no parent, consider it already removed.
                    return child;
                }
                // Normal path
                // @ts-ignore
                return origRemoveChild.call(this, child) as unknown as T;
            } catch {
                // Swallow error to keep React tree stable.
                return child;
            }
        } as unknown as typeof Node.prototype.removeChild;

        // Patch insertBefore to be more tolerant when reference node was moved by Google Translate
        Node.prototype.insertBefore = function patchedInsertBefore(this: Node, newNode: Node, referenceNode: Node | null): Node {
            try {
                // @ts-ignore
                return origInsertBefore.call(this, newNode, referenceNode);
            } catch {
                try {
                    if (referenceNode && referenceNode.parentNode && referenceNode.parentNode !== this) {
                        // @ts-ignore
                        return origInsertBefore.call(referenceNode.parentNode, newNode, referenceNode);
                    }
                    // Fallback: append when reference is invalid
                    // @ts-ignore
                    return origInsertBefore.call(this, newNode, null);
                } catch {
                    return newNode;
                }
            }
        } as unknown as typeof Node.prototype.insertBefore;

        // Patch replaceChild similarly
        Node.prototype.replaceChild = function patchedReplaceChild(this: Node, newChild: Node, oldChild: Node): Node {
            try {
                if (oldChild && oldChild.parentNode !== this) {
                    if (oldChild.parentNode) {
                        // @ts-ignore
                        return origReplaceChild.call(oldChild.parentNode, newChild, oldChild);
                    }
                    return oldChild;
                }
                // @ts-ignore
                return origReplaceChild.call(this, newChild, oldChild);
            } catch {
                return oldChild;
            }
        } as unknown as typeof Node.prototype.replaceChild;

        return () => {
            // Restore originals on unmount
            Node.prototype.removeChild = origRemoveChild;
            Node.prototype.insertBefore = origInsertBefore as unknown as typeof Node.prototype.insertBefore;
            Node.prototype.replaceChild = origReplaceChild as unknown as typeof Node.prototype.replaceChild;
        };
    }, []);

    // Initialize Google Translate once globally
    useEffect(() => {
        const initializeGoogleTranslate = () => {
            window.googleTranslateElementInit = () => {
                if (window.google && window.google.translate) {
                    new window.google.translate.TranslateElement(
                        {
                            pageLanguage: 'en',
                            includedLanguages: 'en,hi,gu',
                            autoDisplay: false,
                        },
                        'global_google_translate_element'
                    );
                    setIsGoogleTranslateReady(true);
                }
            };

            // Load script if not already loaded
            if (!document.querySelector('script[src*="translate.google.com"]')) {
                const script = document.createElement('script');
                script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
                script.async = true;
                document.head.appendChild(script);
            } else if (window.google?.translate) {
                setIsGoogleTranslateReady(true);
            }
        };

        initializeGoogleTranslate();

        // Hide Google Translate UI elements
        const hideGoogleElements = () => {
            const elementsToHide = [
                '.goog-te-banner-frame',
                '.goog-te-ftab',
                '.skiptranslate:not(.goog-te-gadget)',
                '.goog-te-gadget'
            ];

            elementsToHide.forEach(selector => {
                const elements = document.querySelectorAll(selector);
                elements.forEach(el => {
                    (el as HTMLElement).style.display = 'none';
                });
            });

            document.body.style.top = '0px';
            document.body.style.marginTop = '0px';
        };

        const interval = setInterval(hideGoogleElements, 500);

        // Apply saved language on page load
        if (currentLanguage !== 'en') {
            setTimeout(() => {
                triggerTranslation(currentLanguage);
            }, 2000);
        }

        return () => clearInterval(interval);
    }, []);

    const triggerTranslation = (targetLang: string) => {
        try {
            // Method 1: Try using Google Translate's combo box
            const translateCombo = document.querySelector('.goog-te-combo') as HTMLSelectElement;
            if (translateCombo) {
                translateCombo.value = targetLang;
                translateCombo.dispatchEvent(new Event('change'));
                return true;
            }

            // Method 2: URL manipulation
            if (targetLang === 'en') {
                const currentUrl = window.location.href;
                if (currentUrl.includes('googletrans')) {
                    window.location.href = currentUrl.split('#')[0];
                }
            } else {
                const currentUrl = window.location.href.split('#')[0];
                window.location.href = `${currentUrl}#googletrans(en|${targetLang})`;
            }
            return true;
        } catch (error) {
            console.error('Translation failed:', error);
            return false;
        }
    };

    const setLanguage = (langCode: string) => {
        setIsTranslating(true);
        setCurrentLanguage(langCode);

        // Save to storage
        storage.setItemSync(STORAGE_KEYS.SITE_LANGUAGE, langCode);

        // Trigger translation
        const success = triggerTranslation(langCode);

        // Reset translating state
        setTimeout(() => {
            setIsTranslating(false);
            window.location.reload();
        }, success ? 2000 : 500);
    };

    return (
        <TranslationContext.Provider
            value={{
                currentLanguage,
                setLanguage,
                isTranslating,
                languages,
                isGoogleTranslateReady
            }}
        >
            {children}
            {/* Hidden Google Translate Element */}
            <div id="global_google_translate_element" style={{ display: 'none' }}></div>

            {/* Global Styles */}
            <style>{`
                .goog-te-banner-frame,
                .goog-te-ftab,
                .skiptranslate:not(.goog-te-gadget),
                .goog-te-gadget {
                    display: none !important;
                    visibility: hidden !important;
                    height: 0 !important;
                    overflow: hidden !important;
                }
                body {
                    top: 0 !important;
                    margin-top: 0 !important;
                }
                .goog-te-banner-frame.skiptranslate {
                    display: none !important;
                }
            `}</style>
        </TranslationContext.Provider>
    );
};

export const useTranslation = () => {
    const context = useContext(TranslationContext);
    if (context === undefined) {
        throw new Error('useTranslation must be used within a TranslationProvider');
    }
    return context;
};